"use client"

import { useState, useEffect, useCallback } from 'react'

const WINNING_COMBOS = [
  [0, 1, 2], [3, 4, 5], [6, 7, 8], // Rows
  [0, 3, 6], [1, 4, 7], [2, 5, 8], // Columns
  [0, 4, 8], [2, 4, 6]             // Diagonals
]

export default function TicTacToeApp() {
  const [board, setBoard] = useState(Array(9).fill(null))
  const [isXNext, setIsXNext] = useState(true)
  const [gameMode, setGameMode] = useState('ai') // 'ai' | 'pvp'
  const [difficulty, setDifficulty] = useState('hard') // 'easy' | 'medium' | 'hard'
  const [winnerInfo, setWinnerInfo] = useState(null) // { winner: 'X' | 'O' | 'draw', line: [] }
  const [scores, setScores] = useState({ x: 0, o: 0, ties: 0 })
  const [isAiThinking, setIsAiThinking] = useState(false)

  const checkWinner = useCallback((currentBoard) => {
    for (const [a, b, c] of WINNING_COMBOS) {
      if (currentBoard[a] && currentBoard[a] === currentBoard[b] && currentBoard[a] === currentBoard[c]) {
        return { winner: currentBoard[a], line: [a, b, c] }
      }
    }
    if (currentBoard.every((cell) => cell !== null)) {
      return { winner: 'draw', line: [] }
    }
    return null
  }, [])

  // Minimax evaluation for unbeatable AI
  const minimax = useCallback((newBoard, depth, isMaximizing) => {
    const res = checkWinner(newBoard)
    if (res) {
      if (res.winner === 'O') return 10 - depth
      if (res.winner === 'X') return depth - 10
      if (res.winner === 'draw') return 0
    }

    if (isMaximizing) {
      let maxEval = -Infinity
      for (let i = 0; i < 9; i++) {
        if (!newBoard[i]) {
          newBoard[i] = 'O'
          const evalScore = minimax(newBoard, depth + 1, false)
          newBoard[i] = null
          maxEval = Math.max(maxEval, evalScore)
        }
      }
      return maxEval
    } else {
      let minEval = Infinity
      for (let i = 0; i < 9; i++) {
        if (!newBoard[i]) {
          newBoard[i] = 'X'
          const evalScore = minimax(newBoard, depth + 1, true)
          newBoard[i] = null
          minEval = Math.min(minEval, evalScore)
        }
      }
      return minEval
    }
  }, [checkWinner])

  const getBestAiMove = useCallback((currentBoard) => {
    const available = []
    for (let i = 0; i < 9; i++) {
      if (!currentBoard[i]) available.push(i)
    }
    if (available.length === 0) return null

    // Easy: Purely random
    if (difficulty === 'easy') {
      return available[Math.floor(Math.random() * available.length)]
    }

    // Medium: Block immediate wins or take immediate win, otherwise random
    if (difficulty === 'medium') {
      // 1. Can AI win right now?
      for (const idx of available) {
        currentBoard[idx] = 'O'
        if (checkWinner(currentBoard)?.winner === 'O') {
          currentBoard[idx] = null
          return idx
        }
        currentBoard[idx] = null
      }
      // 2. Can player win on next turn? Block it!
      for (const idx of available) {
        currentBoard[idx] = 'X'
        if (checkWinner(currentBoard)?.winner === 'X') {
          currentBoard[idx] = null
          return idx
        }
        currentBoard[idx] = null
      }
      // Otherwise pick center if free or random
      if (available.includes(4)) return 4
      return available[Math.floor(Math.random() * available.length)]
    }

    // Hard: Minimax optimal
    let bestScore = -Infinity
    let bestMove = available[0]

    for (const idx of available) {
      currentBoard[idx] = 'O'
      const score = minimax(currentBoard, 0, false)
      currentBoard[idx] = null
      if (score > bestScore) {
        bestScore = score
        bestMove = idx
      }
    }
    return bestMove
  }, [difficulty, checkWinner, minimax])

  // Handle AI turn
  useEffect(() => {
    if (gameMode === 'ai' && !isXNext && !winnerInfo) {
      setIsAiThinking(true)
      const timer = setTimeout(() => {
        const move = getBestAiMove([...board])
        if (move !== null) {
          const nextBoard = [...board]
          nextBoard[move] = 'O'
          setBoard(nextBoard)

          const win = checkWinner(nextBoard)
          if (win) {
            setWinnerInfo(win)
            if (win.winner === 'O') setScores((s) => ({ ...s, o: s.o + 1 }))
            else if (win.winner === 'draw') setScores((s) => ({ ...s, ties: s.ties + 1 }))
          } else {
            setIsXNext(true)
          }
        }
        setIsAiThinking(false)
      }, 350)

      return () => clearTimeout(timer)
    }
  }, [isXNext, gameMode, winnerInfo, board, getBestAiMove, checkWinner])

  const handleCellClick = (index) => {
    if (board[index] || winnerInfo || isAiThinking) return

    const nextBoard = [...board]
    const currentTurn = isXNext ? 'X' : 'O'
    nextBoard[index] = currentTurn
    setBoard(nextBoard)

    const win = checkWinner(nextBoard)
    if (win) {
      setWinnerInfo(win)
      if (win.winner === 'X') setScores((s) => ({ ...s, x: s.x + 1 }))
      else if (win.winner === 'O') setScores((s) => ({ ...s, o: s.o + 1 }))
      else if (win.winner === 'draw') setScores((s) => ({ ...s, ties: s.ties + 1 }))
    } else {
      setIsXNext(!isXNext)
    }
  }

  const handleRestartRound = () => {
    setBoard(Array(9).fill(null))
    setIsXNext(true)
    setWinnerInfo(null)
    setIsAiThinking(false)
  }

  const handleResetMatch = () => {
    handleRestartRound()
    setScores({ x: 0, o: 0, ties: 0 })
  }

  return (
    <div className="flex flex-col items-center justify-center h-full p-2 select-none">
      <div className="bg-gray-950/80 border border-white/15 rounded-2xl p-4 sm:p-6 shadow-2xl backdrop-blur-md max-w-sm w-full space-y-4">
        {/* Controls: Mode & Difficulty */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex bg-black/40 p-1 rounded-xl border border-white/10 text-xs">
            <button
              onClick={() => { setGameMode('ai'); handleRestartRound(); }}
              className={`px-3 py-1 rounded-lg font-medium transition-colors ${
                gameMode === 'ai' ? 'bg-pink-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              vs AI
            </button>
            <button
              onClick={() => { setGameMode('pvp'); handleRestartRound(); }}
              className={`px-3 py-1 rounded-lg font-medium transition-colors ${
                gameMode === 'pvp' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              2 Players
            </button>
          </div>

          {gameMode === 'ai' && (
            <select
              value={difficulty}
              onChange={(e) => { setDifficulty(e.target.value); handleRestartRound(); }}
              className="bg-black/50 border border-white/10 rounded-lg px-2 py-1 text-xs text-gray-300 focus:outline-none focus:border-pink-500"
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Master</option>
            </select>
          )}
        </div>

        {/* Scoreboard */}
        <div className="grid grid-cols-3 gap-2 bg-black/40 border border-white/10 p-2.5 rounded-xl text-center">
          <div className="p-1 rounded bg-white/5">
            <div className="text-[10px] text-pink-400 font-semibold uppercase tracking-wider">
              {gameMode === 'ai' ? 'Player (X)' : 'Player X'}
            </div>
            <div className="text-lg font-bold text-white">{scores.x}</div>
          </div>
          <div className="p-1 rounded bg-white/5">
            <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">Ties</div>
            <div className="text-lg font-bold text-white">{scores.ties}</div>
          </div>
          <div className="p-1 rounded bg-white/5">
            <div className="text-[10px] text-blue-400 font-semibold uppercase tracking-wider">
              {gameMode === 'ai' ? 'AI (O)' : 'Player O'}
            </div>
            <div className="text-lg font-bold text-white">{scores.o}</div>
          </div>
        </div>

        {/* Status Indicator */}
        <div className="text-center py-1">
          {winnerInfo ? (
            <div className="text-sm font-bold">
              {winnerInfo.winner === 'draw' && <span className="text-amber-300">It is a Draw!</span>}
              {winnerInfo.winner === 'X' && (
                <span className="text-pink-400">
                  {gameMode === 'ai' ? 'You Win!' : 'Player X Wins!'}
                </span>
              )}
              {winnerInfo.winner === 'O' && (
                <span className="text-blue-400">
                  {gameMode === 'ai' ? 'AI Wins!' : 'Player O Wins!'}
                </span>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-300 flex items-center justify-center gap-1.5">
              {isAiThinking ? (
                <>
                  <i className="fas fa-circle-notch fa-spin text-blue-400"></i>
                  <span>AI is calculating move...</span>
                </>
              ) : isXNext ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-pink-500 animate-pulse"></span>
                  <span>Player X's Turn</span>
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
                  <span>{gameMode === 'ai' ? "AI's Turn" : "Player O's Turn"}</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* 3x3 Grid */}
        <div className="grid grid-cols-3 gap-2 bg-black/50 p-2.5 rounded-xl border border-white/10 mx-auto aspect-square max-w-[280px]">
          {board.map((cell, index) => {
            const isWinningCell = winnerInfo?.line?.includes(index)
            return (
              <button
                key={index}
                onClick={() => handleCellClick(index)}
                disabled={Boolean(cell) || Boolean(winnerInfo) || isAiThinking}
                className={`aspect-square rounded-xl flex items-center justify-center text-3xl font-extrabold transition-all ${
                  isWinningCell
                    ? cell === 'X'
                      ? 'bg-pink-600/40 border-2 border-pink-400 text-pink-300 scale-105'
                      : 'bg-blue-600/40 border-2 border-blue-400 text-blue-300 scale-105'
                    : cell
                    ? 'bg-white/5 border border-white/10 cursor-default'
                    : 'bg-white/10 hover:bg-white/20 active:bg-white/25 border border-white/15'
                }`}
              >
                {cell === 'X' && <span className="text-pink-400 font-mono">X</span>}
                {cell === 'O' && <span className="text-blue-400 font-mono">O</span>}
              </button>
            )
          })}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between gap-2 pt-1">
          <button
            onClick={handleRestartRound}
            className="flex-1 py-2 px-3 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5"
          >
            <i className="fas fa-rotate-right text-xs"></i>
            <span>Play Again</span>
          </button>
          <button
            onClick={handleResetMatch}
            className="py-2 px-3 bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white rounded-lg text-xs transition-colors"
            title="Reset match scores"
          >
            <i className="fas fa-trash-can text-xs"></i>
          </button>
        </div>
      </div>
    </div>
  )
}
